import { MODAL_KEY } from '@/constants';
import { AppState, DeliveryMethod, MutationAction, Order } from './types';
import { ProductHierarchy } from '@/hooks/useCatalog';
import { DiscountMinimumRequirement } from '@/helpers/api/DiscountAPI';
import { LineItem } from '@/hooks/useLocalCart';
import { UUID } from 'crypto';
import { DefaultLocalCart } from './appState';
import { ProductReviewsData } from '@/components/CustomerReviews/CustomerReviews';

export const appStateMutations = {
  OPEN_MODAL: (
    state: AppState,
    action: Extract<MutationAction, { type: 'OPEN_MODAL' }>,
  ): AppState => ({
    ...state,
    modal: action.modal,
    modalData: action.modalData,
  }),
  REQUEST_MODAL_CLOSE: (state: AppState): AppState => ({
    ...state,
    modal: null,
  }),
  CLOSE_MODAL: (state: AppState): AppState => ({
    ...state,
    modal: null,
    modalData: undefined,
  }),
  GEN_MAP_SESSION: (
    state: AppState,
    action: Extract<MutationAction, { type: 'GEN_MAP_SESSION' }>,
  ): AppState => ({
    ...state,
    mapbox: { ...state.mapbox, sessionToken: action.token },
  }),
  CLEAR_MAP_SESSION: (
    state: AppState,
    action: Extract<MutationAction, { type: 'CLEAR_MAP_SESSION' }>,
  ): AppState => ({
    ...state,
    mapbox: { ...state.mapbox, sessionToken: null },
  }),
  OPEN_ADDRESS_CONFIRM: (
    state: AppState,
    action: Extract<MutationAction, { type: 'OPEN_ADDRESS_CONFIRM' }>,
  ): AppState => {
    return {
      ...state,
      modal: MODAL_KEY.EDIT_ADDRESS,
      modalData: {
        fullName: action.fullName,
        address: action.address,
        intructions: action.instructions,
        onAddressConfirmed: action.onAddressConfirmed,
      },
    };
  },
  HYDRATE_USER: (
    state: AppState,
    action: Extract<MutationAction, { type: 'HYDRATE_USER' }>,
  ): AppState => {
    return {
      ...state,
      user: action.user,
    };
  },
  CLEAR_USER: (
    state: AppState,
    action: Extract<MutationAction, { type: 'CLEAR_USER' }>,
  ): AppState => {
    return {
      ...state,
      user: null,
    };
  },
  LOCAL_CART_CLEAR: (
    state: AppState,
    action: Extract<MutationAction, { type: 'LOCAL_CART_CLEAR' }>,
  ): AppState => {
    return {
      ...state,
      order: DefaultLocalCart,
    };
  },
  LOCAL_UNFINISHED_CART: (
    state: AppState,
    action: Extract<
      MutationAction,
      { type: 'LOCAL_UNFINISHED_CART'; id: string | null }
    >,
  ): AppState => {
    return {
      ...state,
      order: { ...state.order, id: action.id },
    };
  },
  LOCAL_UPD_LINE_QUANTITY: (
    state: AppState,
    action: Extract<MutationAction, { type: 'LOCAL_UPD_LINE_QUANTITY' }>,
  ): AppState => {
    return {
      ...state,
      order: {
        ...state.order,
        lines: state.order.lines.map((l) =>
          l.id === action.id ? { ...l, quantity: action.newQuantity } : l,
        ),
      },
    };
  },
  LOCAL_UPD_BOX_LINE: (
    state: AppState,
    action: Extract<
      MutationAction,
      {
        type: 'LOCAL_UPD_BOX_LINE';
        localId: UUID;
        contents: LineItem[];
      }
    >,
  ): AppState => ({
    ...state,
    order: {
      ...state.order,
      lines: state.order.lines.map((l) =>
        l.id === action.localId ? { ...l, contents: action.contents } : l,
      ),
    },
  }),

  LOCAL_UPD_LINE_REMOVE: (
    state: AppState,
    action: Extract<MutationAction, { type: 'LOCAL_UPD_LINE_REMOVE' }>,
  ): AppState => {
    return {
      ...state,
      order: {
        ...state.order,
        lines: state.order.lines.filter(
          (l) => l.id !== action.id && l.localId !== action.id,
        ),
      },
    };
  },

  LOCAL_UPD_LINE_ADD: (
    state: AppState,
    action: Extract<MutationAction, { type: 'LOCAL_UPD_LINE_ADD' }>,
  ): AppState => {
    return {
      ...state,
      order: {
        ...state.order,
        lines: [...state.order.lines, action.newItem],
      },
    };
  },
  LOCAL_UPD_RECIPIENT: (
    state: AppState,
    action: Extract<MutationAction, { type: 'LOCAL_UPD_RECIPIENT' }>,
  ): AppState => ({
    ...state,
    order: { ...state.order, recipient: action.recipient },
  }),
  LOADING_PRODUCTS: (
    state: AppState,
    action: Extract<MutationAction, { type: 'LOADING_PRODUCTS' }>,
  ): AppState => ({
    ...state,
    products: { loading: true, ready: false, error: null, data: null },
  }),
  PRODUCTS_READY: (
    state: AppState,
    action: Extract<
      MutationAction,
      { type: 'PRODUCTS_READY'; hierarchy: ProductHierarchy }
    >,
  ): AppState => ({
    ...state,
    products: {
      loading: false,
      ready: true,
      data: action.hierarchy,
      error: null,
    },
  }),
  PRODUCT_FETCH_ERROR: (
    state: AppState,
    action: Extract<
      MutationAction,
      { type: 'PRODUCT_FETCH_ERROR'; err: Error }
    >,
  ): AppState => ({
    ...state,
    products: {
      ...state.products,
      loading: false,
      ready: false,
      data: null,
      error: action.err,
    },
  }),
  CHANGE_ORDER_TYPE: (
    state: AppState,
    action: Extract<
      MutationAction,
      { type: 'CHANGE_ORDER_TYPE'; deliveryMethod: DeliveryMethod }
    >,
  ): AppState => ({
    ...state,
    order: {
      ...state?.order,
      deliveryMethod: action.deliveryMethod,
    } as Order,
  }),
  CHANGE_PICKUP_DATE: (
    state: AppState,
    action: Extract<MutationAction, { type: 'CHANGE_PICKUP_DATE'; date: Date }>,
  ): AppState => ({
    ...state,
    order: {
      ...state?.order,
      pickupDate: action.date,
    } as Order,
  }),
  SET_SHIPPING_DISCOUNT: (
    state: AppState,
    action: Extract<
      MutationAction,
      {
        type: 'SET_SHIPPING_DISCOUNT';
        freeShipping: DiscountMinimumRequirement;
      }
    >,
  ): AppState => ({
    ...state,
    freeShipping: action.freeShipping,
  }),
  SET_REVIEWS_LOADING: (
    state: AppState,
    action: Extract<
      MutationAction,
      { type: 'SET_REVIEWS_LOADING'; productId: string; loading: boolean }
    >,
  ): AppState => ({
    ...state,
    reviews: {
      ...state.reviews,
      data: {
        ...state.reviews.data,
        [action.productId]: {
          ...state.reviews.data[action.productId],
          loading: action.loading,
        },
      },
    },
  }),

  SET_REVIEWS_ERROR: (
    state: AppState,
    action: Extract<
      MutationAction,
      { type: 'SET_REVIEWS_ERROR'; productId: string; err: Error }
    >,
  ): AppState => ({
    ...state,
    reviews: {
      ...state.reviews,
      data: {
        ...state.reviews.data,
        [action.productId]: {
          ...state.reviews.data[action.productId],
          loading: false,
          error: action.err,
        },
      },
    },
  }), // Overwrite existing data (used for initial load, filtering, and sorting)
  SET_REVIEWS_DATA: (
    state: AppState,
    action: Extract<
      MutationAction,
      {
        type: 'SET_REVIEWS_DATA';
        productId: string;
        data: ProductReviewsData;
      }
    >,
  ): AppState => ({
    ...state,
    reviews: {
      ...state.reviews,
      data: {
        ...state.reviews.data,
        [action.productId]: {
          ...state.reviews.data[action.productId], // Preserve existing aggregate if not provided
          list: action.data.reviews,
          count: action.data.totalReviews,
          rating: action.data.averageRating,
          loading: false,
          error: null,
        },
      },
    },
  }),

  // Append new data (used for Infinite Scroll)
  APPEND_REVIEWS_DATA: (
    state: AppState,
    action: Extract<
      MutationAction,
      { type: 'APPEND_REVIEWS_DATA'; productId: string; data: any[] }
    >,
  ): AppState => {
    const existingList = state.reviews.data[action.productId]?.list || [];

    return {
      ...state,
      reviews: {
        ...state.reviews,
        data: {
          ...state.reviews.data,
          [action.productId]: {
            ...state.reviews.data[action.productId],
            list: [...existingList, ...action.data], // Concatenate new page
          },
        },
      },
    };
  },

  // ... rest of mutations
} as const;
